'use client';

import { getProduct, products } from '@/lib/products';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { dilaMenuCopy } from '@/lib/i18n/pages/dilamenu';
import { productsCommonCopy } from '@/lib/i18n/pages/productsCommon';
import { BrowserShot, PhoneShot } from './shots';
import { Reveal } from './Reveal';
import { ProductHero } from './hero';
import {
  AccentGlow,
  DemoBand,
  FeatureRows,
  NextProducts,
  ProductMarquee,
  SectionHead,
  StatGrid,
} from './blocks';

const product = getProduct('dilamenu')!;
const A = product.accent;
const IMG = '/images/products/dilamenu';

const LIVE_MENU = 'https://koshky-khanadan.dila.menu';

/** Real numbers, identical in every locale; labels come from the copy file. */
const MENU_STATS = ['38', '11', '~25kb', '<2s'];

/** Theme preset names are product identifiers, shown in Latin in every locale. */
const THEME_NAMES = [
  'Atelier',
  'Chiaroscuro',
  'Kilim Modern',
  'Brutalist Grotesque',
  'Solar Flare',
  'Mono-Chef',
  'Modern Bistro',
  'Steakhouse',
  'Hookah Lounge',
  'Bakery',
  'Traditional Kurdish',
];

/** Client venue names are proper nouns, shown in Latin in every locale. */
const CLIENTS = [
  'Koshky Khanadan',
  'Arbab Cafe',
  'Swan Village',
  'Happy Time',
  'Hama Cafe',
  'Kabab Najar',
  'Sport Cafe',
  'Chilay',
];

export function DilaMenuPage() {
  const { locale, href } = useLocale();
  const t = pageCopy(dilaMenuCopy, locale);
  const c = pageCopy(productsCommonCopy, locale);
  const cursor = { goLabel: c.cursorGo, openLabel: c.cursorOpen };
  return (
    <div className="self-styled relative text-white">
      {/* ── Hero: phone fan over food ────────────────────────────────── */}
      <ProductHero
        name="dila.menu"
        accent={A}
        title={t.hero.title}
        lede={t.hero.lede}
        secondary={{ label: t.hero.secondary, href: LIVE_MENU, external: true }}
        liveHref={product.liveUrl}
        liveLabel={
          <>
            {t.hero.liveLabel} · <bdi dir="ltr" className="keep-latin">{product.domain}</bdi>
          </>
        }
        breadcrumbLabel={c.breadcrumbProducts}
        productsHref={href('/products')}
        bookLabel={c.bookDemo}
        bookHref={href('/contact')}
        {...cursor}
      >
        <AccentGlow accent={A} className="start-1/3 top-1/4 h-80 w-80" opacity={0.2} />
        <div className="relative mx-auto h-[520px] max-w-[540px] sm:h-[580px]">
          <div
            className="absolute inset-x-10 top-4 bottom-4 -rotate-2 overflow-hidden rounded-2xl border border-white/10 bg-cover bg-center opacity-45"
            style={{ backgroundImage: `url(${IMG}/food-koshky.webp)` }}
            aria-hidden="true"
          />
          <div className="absolute start-0 top-16 hidden w-[34%] -rotate-[8deg] rtl:rotate-[8deg] sm:block">
            <PhoneShot
              src={`${IMG}/welcome.webp`}
              alt={t.hero.welcomeAlt}
              sizes="18vw"
            />
          </div>
          <div className="absolute left-1/2 top-0 z-10 w-[48%] -translate-x-1/2 sm:w-[42%]">
            <PhoneShot src={product.image} alt={t.hero.imageAlt} priority sizes="(max-width: 640px) 48vw, 22vw" />
          </div>
          <div className="absolute end-0 top-20 hidden w-[32%] rotate-[8deg] rtl:-rotate-[8deg] sm:block">
            <PhoneShot
              src={`${IMG}/item-modal.webp`}
              alt={t.hero.itemAlt}
              sizes="17vw"
            />
          </div>
        </div>
      </ProductHero>

      {/* ── Stats strip ──────────────────────────────────────────────── */}
      <section aria-label={t.stats.aria} className="border-t border-white/10 px-6 py-20 md:px-12 md:py-24 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <StatGrid
              accent={A}
              stats={MENU_STATS.map((value, i) => ({ value, label: t.stats.labels[i] }))}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Themes (centered stack + the one marquee) ────────────────── */}
      <section aria-label={t.themes.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title={t.themes.title}
              lede={t.themes.lede}
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120} className="relative mt-14">
            <AccentGlow accent={A} className="end-1/4 -top-8 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/themes.webp`}
              alt={t.themes.shotAlt}
              url="dila.menu/themes"
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
        </div>
        <ProductMarquee
          accent={A}
          className="mt-16"
          duration={44}
          items={THEME_NAMES}
        />
      </section>

      {/* ── Ordering (split: copy left, shot right) ──────────────────── */}
      <section aria-label={t.ordering.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <SectionHead
              accent={A}
              title={t.ordering.title}
              lede={t.ordering.lede}
            />
            <FeatureRows accent={A} className="mt-10" rows={t.ordering.rows} />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-end-8 bottom-0 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/pos-ku.webp`}
              alt={t.ordering.shotAlt}
              url={t.ordering.shotUrl}
              accent={A}
              aspect="aspect-[4/3]"
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── LivePulse (split: shot left, copy right) ─────────────────── */}
      <section aria-label={t.pulse.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="relative order-2 lg:order-1 lg:col-span-6" delay={120}>
            <AccentGlow accent={A} className="-start-8 top-10 h-64 w-64" opacity={0.16} />
            <div className="mx-auto max-w-[380px]">
              <PhoneShot
                src={`${IMG}/livepulse.webp`}
                alt={t.pulse.shotAlt}
                sizes="(max-width: 1024px) 80vw, 30vw"
              />
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2 lg:col-span-6">
            <SectionHead
              accent={A}
              title={t.pulse.title}
              lede={t.pulse.lede}
            />
            <FeatureRows accent={A} className="mt-10" rows={t.pulse.rows} />
          </Reveal>
        </div>
      </section>

      {/* ── Multi-vertical bento ─────────────────────────────────────── */}
      <section aria-label={t.beyond.aria} className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title={t.beyond.title}
              lede={t.beyond.lede}
            />
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <Reveal>
              <BrowserShot
                src={`${IMG}/barber.webp`}
                alt={t.beyond.barberAlt}
                url="dila.menu"
                accent={A}
                aspect="aspect-[4/5]"
                sizes="(max-width: 768px) 100vw, 45vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] rtl:tracking-normal text-white/45">
                {t.beyond.barberCaption}
              </p>
            </Reveal>
            <Reveal delay={120} className="md:pt-16">
              <BrowserShot
                src={`${IMG}/order.webp`}
                alt={t.beyond.laundryAlt}
                url="dila.menu"
                accent={A}
                aspect="aspect-[4/5]"
                sizes="(max-width: 768px) 100vw, 45vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] rtl:tracking-normal text-white/45">
                {t.beyond.laundryCaption}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Food band (editorial moment) ─────────────────────────────── */}
      <section aria-label={t.craft.aria} className="relative overflow-hidden border-t border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG}/food-swan.webp)` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
        <div className="relative px-6 py-32 md:px-12 md:py-44 lg:px-24">
          <Reveal className="mx-auto max-w-[1400px]">
            <p className="rtl-display-m max-w-3xl font-six-caps text-6xl lowercase leading-[0.92] text-white sm:text-7xl md:text-8xl">
              {t.craft.text}
            </p>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] rtl:tracking-normal" style={{ color: A }}>
              {t.craft.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── dila RMS band ────────────────────────────────────────────── */}
      <section aria-label={t.rms.aria} className="px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px] rounded-3xl border border-white/10 bg-white/[0.03] p-7 md:p-12 lg:p-16">
          <Reveal>
            <span className="eyebrow" style={{ color: A }}>
              {t.rms.eyebrow}
            </span>
            <h2 className="rtl-display-m mt-6 max-w-3xl font-six-caps text-6xl lowercase leading-[0.9] text-white sm:text-7xl md:text-8xl">
              {t.rms.title}
            </h2>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/65 md:text-lg">
              {t.rms.lede}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7" delay={100}>
              <BrowserShot
                src={`${IMG}/pos-tables.webp`}
                alt={t.rms.tablesAlt}
                url={t.rms.tablesUrl}
                accent={A}
                aspect="aspect-[16/10]"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </Reveal>
            <Reveal className="lg:col-span-5 lg:pt-14" delay={200}>
              <BrowserShot
                src={`${IMG}/pos-platforms.webp`}
                alt={t.rms.platformsAlt}
                url={t.rms.platformsUrl}
                accent={A}
                aspect="aspect-[4/3]"
                sizes="(max-width: 1024px) 100vw, 38vw"
              />
            </Reveal>
          </div>
          <Reveal delay={150}>
            <FeatureRows accent={A} className="mt-14" rows={t.rms.rows} />
          </Reveal>
        </div>
      </section>

      {/* ── Clients strip ────────────────────────────────────────────── */}
      <section aria-label={t.clients.aria} className="border-t border-white/10 px-6 py-20 md:px-12 md:py-24 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] rtl:tracking-normal text-white/45">
              {t.clients.label}
            </p>
            <ul className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
              {CLIENTS.map((name) => (
                <li
                  key={name}
                  className="keep-latin flex items-center gap-8 font-six-caps text-4xl lowercase leading-none text-white/75 sm:text-5xl"
                >
                  {name}
                  <span style={{ color: A }} aria-hidden="true" className="text-2xl">
                    ✦
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── Demo + cross-sell ────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="dila.menu"
            title={t.demo.title}
            sub={t.demo.sub}
            primary={{ label: c.bookDemo, href: href('/contact') }}
            secondary={{ label: t.demo.secondary, href: LIVE_MENU, external: true }}
            accent={A}
            eyebrow={c.bookDemo}
            note={
              <>
                {t.demo.liveNote}:{' '}
                <bdi dir="ltr" className="keep-latin">
                  dila.menu · koshky-khanadan.dila.menu · demo-restaurant.dila.menu
                </bdi>
              </>
            }
            {...cursor}
          />
          <NextProducts
            className="mb-24"
            ariaLabel={c.otherProductsAria}
            viewLabel={c.cursorView}
            href={href}
            items={products
              .filter((p) => p.slug !== 'dilamenu')
              .map((p) => ({
                slug: p.slug,
                name: p.name,
                tagline: c.taglines[p.slug],
                accent: p.accent,
              }))}
          />
        </div>
      </div>
    </div>
  );
}
